import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Store,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Shield,
  Search,
  Eye,
  Ban,
  DollarSign,
  Package,
  Star,
  Building2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  TrendingUp,
  AlertTriangle,
  LogIn,
  ExternalLink,
  Wallet,
  CreditCard
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminMarketplace() {
  const { token } = useAuth();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    try {
      const res = await axios.get(`${API}/api/sellers/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSellers(res.data);
    } catch (err) {
      toast.error("Kunne ikke laste selgere");
    } finally {
      setLoading(false);
    }
  };

  const approveSeller = async (sellerId) => {
    try {
      await axios.put(`${API}/api/sellers/admin/${sellerId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Selger godkjent!");
      fetchSellers();
      setDetailsOpen(false);
    } catch (err) {
      toast.error("Kunne ikke godkjenne selger");
    }
  };

  const rejectSeller = async (sellerId, reason = "") => {
    try {
      await axios.put(`${API}/api/sellers/admin/${sellerId}/reject?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Selger avslått");
      fetchSellers();
      setDetailsOpen(false);
    } catch (err) {
      toast.error("Kunne ikke avslå selger");
    }
  };

  const suspendSeller = async (sellerId, reason = "") => {
    try {
      await axios.put(`${API}/api/sellers/admin/${sellerId}/suspend?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Selger suspendert");
      fetchSellers();
      setDetailsOpen(false);
    } catch (err) {
      toast.error("Kunne ikke suspendere selger");
    }
  };

  const verifySeller = async (sellerId) => {
    try {
      await axios.put(`${API}/api/sellers/admin/${sellerId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Selger verifisert!");
      fetchSellers();
      setDetailsOpen(false);
    } catch (err) {
      toast.error("Kunne ikke verifisere selger");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-yellow-500/20 text-yellow-400"><Clock className="w-3 h-3 mr-1" /> Venter</Badge>;
      case "approved":
        return <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="w-3 h-3 mr-1" /> Godkjent</Badge>;
      case "rejected":
        return <Badge className="bg-red-500/20 text-red-400"><XCircle className="w-3 h-3 mr-1" /> Avslått</Badge>;
      case "suspended":
        return <Badge className="bg-orange-500/20 text-orange-400"><Ban className="w-3 h-3 mr-1" /> Suspendert</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const filteredSellers = sellers.filter(s => {
    const matchesSearch = s.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.seller_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.contact_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = activeTab === "all" || s.status === activeTab;
    return matchesSearch && matchesTab;
  });

  const pendingCount = sellers.filter(s => s.status === "pending").length;
  const approvedCount = sellers.filter(s => s.status === "approved").length;
  const suspendedCount = sellers.filter(s => s.status === "suspended").length;

  if (loading) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Marketplace Admin">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={adminNavItems} title="Marketplace Admin">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#F7931A]/20 rounded-lg flex items-center justify-center">
                  <Store className="w-5 h-5 text-[#F7931A]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{sellers.length}</p>
                  <p className="text-[#94A3B8] text-sm">Totalt Selgere</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{pendingCount}</p>
                  <p className="text-[#94A3B8] text-sm">Venter Godkjenning</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{approvedCount}</p>
                  <p className="text-[#94A3B8] text-sm">Aktive Selgere</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <Ban className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{suspendedCount}</p>
                  <p className="text-[#94A3B8] text-sm">Suspendert</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Alert */}
        {pendingCount > 0 && (
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-300">
                {pendingCount} selger{pendingCount > 1 ? 'e' : ''} venter på godkjenning
              </span>
              <Button 
                size="sm" 
                onClick={() => setActiveTab("pending")}
                className="ml-auto bg-yellow-500 text-black hover:bg-yellow-400"
              >
                Se søknader
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Sellers List */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[#23F7DD]" />
                Selgere
              </CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Søk selgere..."
                  className="pl-9 bg-black/50 border-white/10 text-white"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-white/5 border-white/10 mb-4">
                <TabsTrigger value="pending" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
                  Venter ({pendingCount})
                </TabsTrigger>
                <TabsTrigger value="approved" className="data-[state=active]:bg-green-500 data-[state=active]:text-black">
                  Godkjent ({approvedCount})
                </TabsTrigger>
                <TabsTrigger value="suspended" className="data-[state=active]:bg-orange-500 data-[state=active]:text-black">
                  Suspendert ({suspendedCount})
                </TabsTrigger>
                <TabsTrigger value="all" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-black">
                  Alle ({sellers.length})
                </TabsTrigger>
              </TabsList>

              {filteredSellers.length === 0 ? (
                <div className="text-center py-12 text-[#94A3B8]">
                  <Store className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Ingen selgere funnet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredSellers.map(seller => (
                    <div 
                      key={seller.seller_id}
                      className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        {seller.logo_url ? (
                          <img 
                            src={seller.logo_url} 
                            alt={seller.company_name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-[#F7931A]/20 flex items-center justify-center">
                            <Store className="w-6 h-6 text-[#F7931A]" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">{seller.company_name}</p>
                            {seller.verified && (
                              <Shield className="w-4 h-4 text-blue-400" title="Verifisert" />
                            )}
                          </div>
                          <p className="text-[#94A3B8] text-sm font-mono">{seller.seller_code}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        <div className="text-right hidden md:block">
                          <p className="text-white">{seller.total_products || 0} produkter</p>
                          <p className="text-[#94A3B8] text-sm">
                            {(seller.total_revenue || 0).toLocaleString()} NOK
                          </p>
                        </div>
                        
                        {getStatusBadge(seller.status)}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setSelectedSeller(seller); setDetailsOpen(true); }}
                          className="border-white/20 text-white hover:bg-white/10"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Seller Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedSeller && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {selectedSeller.logo_url ? (
                    <img 
                      src={selectedSeller.logo_url} 
                      alt={selectedSeller.company_name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-[#F7931A]/20 flex items-center justify-center">
                      <Store className="w-6 h-6 text-[#F7931A]" />
                    </div>
                  )}
                  <div>
                    <p className="text-white">{selectedSeller.company_name}</p>
                    <p className="text-[#94A3B8] text-sm font-mono">{selectedSeller.seller_code}</p>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Status */}
                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedSeller.status)}
                  {selectedSeller.verified && (
                    <Badge className="bg-blue-500/20 text-blue-400">
                      <Shield className="w-3 h-3 mr-1" /> Verifisert
                    </Badge>
                  )}
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-2 text-[#94A3B8] text-sm mb-1">
                      <Mail className="w-4 h-4" /> E-post
                    </div>
                    <p className="text-white">{selectedSeller.contact_email}</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-2 text-[#94A3B8] text-sm mb-1">
                      <Phone className="w-4 h-4" /> Telefon
                    </div>
                    <p className="text-white">{selectedSeller.contact_phone || '-'}</p>
                  </div>
                  {selectedSeller.org_number && (
                    <div className="p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-2 text-[#94A3B8] text-sm mb-1">
                        <Building2 className="w-4 h-4" /> Org.nr
                      </div>
                      <p className="text-white">{selectedSeller.org_number}</p>
                    </div>
                  )}
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-2 text-[#94A3B8] text-sm mb-1">
                      <Calendar className="w-4 h-4" /> Registrert
                    </div>
                    <p className="text-white">
                      {new Date(selectedSeller.created_at).toLocaleDateString('no-NO')}
                    </p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="p-3 bg-white/5 rounded-lg text-center">
                    <Package className="w-5 h-5 mx-auto mb-1 text-[#23F7DD]" />
                    <p className="text-xl font-bold text-white">{selectedSeller.total_products || 0}</p>
                    <p className="text-[#94A3B8] text-xs">Produkter</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg text-center">
                    <TrendingUp className="w-5 h-5 mx-auto mb-1 text-[#F7931A]" />
                    <p className="text-xl font-bold text-white">{selectedSeller.total_sales || 0}</p>
                    <p className="text-[#94A3B8] text-xs">Salg</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg text-center">
                    <DollarSign className="w-5 h-5 mx-auto mb-1 text-green-400" />
                    <p className="text-xl font-bold text-white">
                      {((selectedSeller.total_revenue || 0) / 1000).toFixed(0)}k
                    </p>
                    <p className="text-[#94A3B8] text-xs">NOK</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg text-center">
                    <Star className="w-5 h-5 mx-auto mb-1 text-yellow-400" />
                    <p className="text-xl font-bold text-white">{selectedSeller.rating || 0}</p>
                    <p className="text-[#94A3B8] text-xs">Rating</p>
                  </div>
                </div>

                {/* Description */}
                {selectedSeller.description && (
                  <div className="p-3 bg-white/5 rounded-lg">
                    <p className="text-[#94A3B8] text-sm mb-1">Beskrivelse</p>
                    <p className="text-white">{selectedSeller.description}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-white/10">
                  {selectedSeller.status === "pending" && (
                    <>
                      <Button
                        onClick={() => approveSeller(selectedSeller.seller_id)}
                        className="flex-1 bg-green-500 text-white hover:bg-green-600"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Godkjenn
                      </Button>
                      <Button
                        onClick={() => rejectSeller(selectedSeller.seller_id)}
                        variant="outline"
                        className="flex-1 border-red-500 text-red-400 hover:bg-red-500/10"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Avslå
                      </Button>
                    </>
                  )}
                  
                  {selectedSeller.status === "approved" && (
                    <>
                      {!selectedSeller.verified && (
                        <Button
                          onClick={() => verifySeller(selectedSeller.seller_id)}
                          className="flex-1 bg-blue-500 text-white hover:bg-blue-600"
                        >
                          <Shield className="w-4 h-4 mr-2" />
                          Verifiser
                        </Button>
                      )}
                      <Button
                        onClick={() => suspendSeller(selectedSeller.seller_id)}
                        variant="outline"
                        className="flex-1 border-orange-500 text-orange-400 hover:bg-orange-500/10"
                      >
                        <Ban className="w-4 h-4 mr-2" />
                        Suspender
                      </Button>
                    </>
                  )}

                  {selectedSeller.status === "suspended" && (
                    <Button
                      onClick={() => approveSeller(selectedSeller.seller_id)}
                      className="flex-1 bg-green-500 text-white hover:bg-green-600"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Aktiver igjen
                    </Button>
                  )}
                </div>
                
                {/* Admin Quick Actions */}
                <div className="pt-4 border-t border-white/10 space-y-3">
                  <p className="text-[#94A3B8] text-sm font-medium">Admin Handlinger</p>
                  <div className="grid grid-cols-2 gap-2">
                    <a 
                      href={`/nettbutikk/seller/${selectedSeller.seller_code}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        variant="outline"
                        className="w-full border-white/20 text-white hover:bg-white/10"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Se Butikk
                      </Button>
                    </a>
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          // Save current admin token for restore later
                          const currentToken = localStorage.getItem("scaffoldx_token");
                          if (currentToken) {
                            localStorage.setItem("admin_token_backup", currentToken);
                          }

                          // Call backend impersonation endpoint
                          const res = await axios.post(
                            `${API}/api/admin/impersonate/${selectedSeller.user_id}`,
                            {},
                            { headers: { Authorization: `Bearer ${currentToken}` } }
                          );

                          // Store new seller token (key MUST be scaffoldx_token to match AuthContext)
                          localStorage.setItem("scaffoldx_token", res.data.token);
                          localStorage.setItem(
                            "impersonation_info",
                            JSON.stringify({
                              admin_user_id: res.data.impersonated_by?.user_id,
                              admin_name: res.data.impersonated_by?.name,
                              seller_id: selectedSeller.seller_id,
                              seller_code: selectedSeller.seller_code,
                              company_name: selectedSeller.company_name,
                              target_user: res.data.user,
                            })
                          );

                          toast.success(`Innlogget som ${selectedSeller.company_name}`);
                          // Hard reload so AuthContext re-reads new token
                          window.location.href = "/seller/dashboard";
                        } catch (err) {
                          toast.error(
                            err.response?.data?.detail || "Kunne ikke logge inn som selger"
                          );
                        }
                      }}
                      className="w-full border-[#F7931A]/50 text-[#F7931A] hover:bg-[#F7931A]/10"
                      data-testid="admin-login-as-seller-btn"
                    >
                      <LogIn className="w-4 h-4 mr-2" />
                      Login Som Selger
                    </Button>
                  </div>
                  
                  {/* Payment Info */}
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[#94A3B8] text-sm">Betalingsmetode</span>
                      <Badge className={selectedSeller.payment_method === 'stripe' ? 'bg-[#635BFF]/20 text-[#635BFF]' : 'bg-[#F7931A]/20 text-[#F7931A]'}>
                        {selectedSeller.payment_method === 'stripe' ? (
                          <><CreditCard className="w-3 h-3 mr-1" /> Stripe</>
                        ) : (
                          <><Wallet className="w-3 h-3 mr-1" /> Faktura</>
                        )}
                      </Badge>
                    </div>
                    {selectedSeller.payment_method === 'invoice' && selectedSeller.bank_account?.account_number && (
                      <p className="text-white text-sm font-mono">
                        Konto: {selectedSeller.bank_account.account_number}
                      </p>
                    )}
                    {selectedSeller.stripe_onboarded && (
                      <p className="text-green-400 text-sm flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Stripe tilkoblet
                      </p>
                    )}
                  </div>
                  
                  {/* Payout Info */}
                  <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-[#94A3B8] text-sm">Ventende utbetaling</span>
                      <span className="text-green-400 font-bold">
                        {((selectedSeller.total_revenue || 0) - (selectedSeller.total_paid || 0)).toLocaleString()} NOK
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
